import { Link } from 'react-router-dom';
import { Users, Video, Sparkles, TrendingUp, Plus } from 'lucide-react';
import { usePersonas } from '../hooks/usePersona';
import { useProjects } from '../hooks/useProject';
import Card, { CardHeader } from '../components/common/Card';
import Button from '../components/common/Button';

export default function Dashboard() {
  const { personas, isLoading: personasLoading } = usePersonas();
  const { projects, isLoading: projectsLoading } = useProjects();

  const stats = [
    {
      name: 'Total Personas',
      value: personas.length,
      icon: Users,
      color: 'text-blue-400',
      bg: 'bg-blue-400/10',
    },
    {
      name: 'Video Projects',
      value: projects.length,
      icon: Video,
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
    },
    {
      name: 'Videos Generated',
      value: projects.filter((p) => p.status === 'complete').length,
      icon: Sparkles,
      color: 'text-jelly-400',
      bg: 'bg-jelly-400/10',
    },
    {
      name: 'This Month',
      value: projects.filter((p) => {
        const date = new Date(p.created_at);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }).length,
      icon: TrendingUp,
      color: 'text-green-400',
      bg: 'bg-green-400/10',
    },
  ];

  const recentProjects = projects.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-zinc-400 mt-1">Welcome back! Here's what's happening.</p>
        </div>
        <Link to="/admin/personas/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            New Persona
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.name} padding="sm">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-zinc-400">{stat.name}</p>
                <p className="text-2xl font-bold">{personasLoading || projectsLoading ? '...' : stat.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Personas */}
        <Card>
          <CardHeader
            title="Recent Personas"
            description="Your artist personas"
            action={
              <Link to="/admin/personas" className="text-sm text-jelly-400 hover:text-jelly-300">
                View all
              </Link>
            }
          />
          {personasLoading ? (
            <div className="text-zinc-500 text-center py-8">Loading...</div>
          ) : personas.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No personas yet</p>
              <Link to="/admin/personas/new" className="text-jelly-400 text-sm hover:text-jelly-300">
                Create your first persona
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {personas.slice(0, 4).map((persona) => (
                <Link
                  key={persona.id}
                  to={`/admin/personas/${persona.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-jelly-500 to-purple-600 flex items-center justify-center text-lg font-bold">
                    {persona.artist_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{persona.artist_name}</p>
                    <p className="text-sm text-zinc-400 truncate">{persona.stage_name || 'No stage name'}</p>
                  </div>
                  <span className="text-xs text-zinc-500">{persona.creation_method}</span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Projects */}
        <Card>
          <CardHeader
            title="Recent Projects"
            description="Your video projects"
            action={
              <Link to="/admin/projects" className="text-sm text-jelly-400 hover:text-jelly-300">
                View all
              </Link>
            }
          />
          {projectsLoading ? (
            <div className="text-zinc-500 text-center py-8">Loading...</div>
          ) : recentProjects.length === 0 ? (
            <div className="text-center py-8">
              <Video className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No projects yet</p>
              <p className="text-zinc-500 text-sm">Create a persona first</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/admin/projects/${project.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                    <Video className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{project.target_platform}</p>
                    <p className="text-sm text-zinc-400">{project.target_duration}s • {project.quality_tier}</p>
                  </div>
                  <StatusBadge status={project.status} />
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-zinc-700 text-zinc-300',
    composing: 'bg-blue-900/50 text-blue-400',
    generating: 'bg-purple-900/50 text-purple-400',
    processing: 'bg-yellow-900/50 text-yellow-400',
    complete: 'bg-green-900/50 text-green-400',
    failed: 'bg-red-900/50 text-red-400',
    cancelled: 'bg-zinc-700 text-zinc-400',
  };

  return <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.pending}`}>{status}</span>;
}
